import React, { useState } from "react";
import { notifySuccess } from "src/assets/toastService";
import PopupModules from "src/features/auth/components/PopupModules";
import { putCancelOrder } from "src/shared/services/services";
import { errorHandling } from "src/shared/utils/errorHandling";
import { Button } from "../../components/Button";
import { Order } from "src/shared/types";

type Props = {
  openCancel: boolean;
  closePop: () => void;
  osb: number;
  order: Order;
  received: number;
  close: () => void;
};

function PaymentReverse({
  openCancel,
  closePop,
  osb,
  order,
  received,
  close,
}: Props) {
  const [method, setMethod] = useState<"refund" | "deduct_from_due">("refund");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── New interface field paths ─────────────────────────────────────────────
  // finalAmount and remainingAmount now live in orders[0]
  const transaction = order?.orders?.[0];
  const finalAmount = Number(transaction?.finalAmount ?? 0);
  const remainingAmount = Number(transaction?.remainingAmount ?? 0);

  // OSB from ledger
  const totalOutstanding = Number(osb ?? 0);

  // Cancel amount = what the customer already paid
  const cancelAmount = received;

  // New balance after cancellation
  const newbalance = totalOutstanding - received - remainingAmount;

  // Deduct from OSB is not allowed if OSB < remainingAmount
  const isDeductDisabled = totalOutstanding <= remainingAmount;

  const excessAmount = newbalance <= 0 ? Math.abs(newbalance) : 0;
  const newOsb = newbalance > 0 ? Math.abs(newbalance) : 0;

  const confirmCancel = () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const putData = {
        orderStatus: "Cancelled",
        adjustType: method,
      };
      // orderId replaces old Orderid
      putCancelOrder(order.orderId, putData);
      notifySuccess("Order has been Cancelled");
      setTimeout(() => {
        close();
      }, 500);
    } catch (err) {
      errorHandling(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopupModules
      isOpen={openCancel}
      onClose={close}
      title="Cancel Order"
      back={true}
      onBack={closePop}
    >
      <div className="p-4 space-y-4">
        {/* Subtitle */}
        <p className="text-center text-gray-700 font-semibold">
          This action will cancel the order.
          <br />
          Click Confirm to Cancel the Order.
        </p>

        <h3 className="text-center">
          Outstanding Balance = {totalOutstanding.toFixed(2)}
        </h3>

        {/* Radio Options */}
        <div className="flex items-center justify-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="method"
              checked={method === "refund"}
              onChange={() => setMethod("refund")}
              className="w-4 h-4"
            />
            <span>Refund</span>
          </label>

          <label
            className={`flex items-center gap-2 ${isDeductDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
              }`}
          >
            <input
              type="radio"
              name="method"
              checked={method === "deduct_from_due"}
              onChange={() => setMethod("deduct_from_due")}
              className="w-4 h-4"
              disabled={isDeductDisabled}
            />
            <span>Deduct from Outstanding</span>
          </label>
        </div>

        {/* Warning when deduct is disabled */}
        {isDeductDisabled && (
          <div className="flex flex-col justify-center items-center text-red-800 text-center border border-red-200 bg-red-50 p-3 rounded shadow-sm">
            <p>
              Deduction from OSB is not allowed because the customer balance is
              less than the required amount.
            </p>
            <p>Please go to the ledger to adjust the balance.</p>
          </div>
        )}

        {/* Deduct from OSB breakdown */}
        {method === "deduct_from_due" && (
          <div className="bg-gray-50 p-4 rounded-lg border shadow-sm text-sm space-y-2">
            <Row
              label="Total Outstanding Balance"
              value={`${totalOutstanding.toFixed(2)} QAR`}
            />
            <Row
              label="Order Final Amount"
              value={`${finalAmount.toFixed(2)} QAR`}
            />
            <Row
              label="Order Cancel Amount"
              value={`${cancelAmount.toFixed(2)} QAR`}
            />
            <Row
              label="Excess Amount"
              value={`${excessAmount.toFixed(2)} QAR`}
            />
            <Row label="New OSB" value={`${newOsb.toFixed(2)} QAR`} />

            <div className="bg-red-50 border border-red-200 p-3 mt-3 rounded text-red-700">
              <strong>Note:</strong>
              <p>Customer has an excess amount.</p>
              <p>
                To return or refund it, please go to the Ledger page and use
                the Update Refund option.
              </p>
            </div>
          </div>
        )}

        {/* Refund summary */}
        {method === "refund" && (
          <div className="text-center text-red-600 text-xl font-semibold">
            Refund Amount: {cancelAmount.toFixed(2)} QAR
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center gap-4 pt-4">
          <Button label="Cancel" onClick={close} variant="cancel" />
          <Button
            label={isSubmitting ? "Processing..." : "Confirm"}
            onClick={confirmCancel}
            disabled={isSubmitting}
            variant="ThemeBase"
          />
        </div>
      </div>
    </PopupModules>
  );
}

export default PaymentReverse;

// ── Reusable Row ──────────────────────────────────────────────────────────────
const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between text-gray-700">
    <span>{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);